import { FC, useMemo, useState } from 'react';
import { useFileGetFileHook } from 'api/file/file';
import ErrorTryAgain from 'components/ErrorTryAgain';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import styled from 'styled-components';
import { RequestStatus, useResponseFn } from 'hooks/useApiCallFn';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from 'components/inputs/Button';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Image = styled.img`
  max-width: calc(80vw - 100px);
  max-height: calc(80vh - 100px);
`;

const ImageError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  font-weight: 500;
`;

const PDFBrowserPanel = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 20px;
  font-weight: 600;
`;

interface PDFViewerProps {
  file: string;
  maxHeight?: number;
  browsable?: boolean;
}

const PDFViewer: FC<PDFViewerProps> = ({ file, maxHeight, browsable }) => {
  const [numPages, setNumPages] = useState<number>(1);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const goBack = () => {
    setPageIndex((prev) => Math.max(prev - 1, 0));
  };

  const goForth = () => {
    setPageIndex((prev) => Math.min(prev + 1, numPages - 1));
  };

  return (
    <Document
      file={file}
      onLoadSuccess={async (pdf) => {
        setNumPages(await pdf.numPages);
      }}
    >
      <Page height={maxHeight} pageIndex={pageIndex} renderTextLayer={false} />
      {browsable && (
        <PDFBrowserPanel>
          <Button
            disabled={pageIndex <= 0}
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={goBack}
          />
          <span>
            {pageIndex + 1} / {numPages}
          </span>
          <Button
            disabled={pageIndex >= numPages - 1}
            icon={<FontAwesomeIcon icon={faChevronRight} />}
            onClick={goForth}
          />
        </PDFBrowserPanel>
      )}
    </Document>
  );
};

interface ImageFileProps {
  imageB64Url: string;
  fileEnding?: string;
  maxHeight?: number;
}

const ImageFile: FC<ImageFileProps> = ({
  imageB64Url,
  fileEnding,
  maxHeight,
}) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <ImageError>
        <span>Bilden kunde inte visas i webbläsaren.</span>
        <a download={`bild.${fileEnding ?? 'jpg'}`} href={imageB64Url}>
          Ladda ner bilden
        </a>
      </ImageError>
    );
  }

  return (
    <Image
      alt="Bild"
      draggable={false}
      height={maxHeight}
      onError={() => setFailed(true)}
      src={imageB64Url}
    />
  );
};

interface FileProps {
  imageB64Url: string;
  mimeType: string;
  fileEnding?: string;
  maxHeight?: number;
  browsable?: boolean;
}
export const FileViewer: FC<FileProps> = ({
  imageB64Url,
  mimeType,
  fileEnding,
  maxHeight,
  browsable,
}) => {
  // const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  switch (mimeType) {
    case 'application/pdf':
      return (
        <PDFViewer
          browsable={browsable}
          file={imageB64Url}
          maxHeight={maxHeight}
        />
      );
    default:
      return (
        <ImageFile
          fileEnding={fileEnding}
          imageB64Url={imageB64Url}
          maxHeight={maxHeight}
        />
      );
  }
};

interface Props {
  fileGuid: string;
  fileEnding: string;
  maxHeight?: number;
  browsable?: boolean;
  container?: string;
}

export const GenericFileViewer: FC<Props> = ({
  fileGuid,
  fileEnding,
  maxHeight,
  browsable,
  container,
}) => {
  const getFile = useFileGetFileHook();
  const {
    response: imageResponse,
    status,
    update,
  } = useResponseFn(
    () => getFile({ guid: fileGuid, fileEnding, container }),
    [fileGuid, fileEnding, container]
  );

  const imageB64Url = useMemo(() => {
    if (imageResponse) {
      return window.URL.createObjectURL(imageResponse);
    }
    return undefined;
  }, [imageResponse]);

  switch (status) {
    case RequestStatus.Idle:
    case RequestStatus.Fetching:
      return <LoadingSpinner />;

    case RequestStatus.Fetched:
      if (imageB64Url) {
        return (
          <FileViewer
            browsable={browsable}
            fileEnding={fileEnding}
            imageB64Url={imageB64Url}
            maxHeight={maxHeight}
            mimeType={
              fileEnding === 'pdf' ? 'application/pdf' : `image/${fileEnding}`
            }
          />
        );
      }
      return null;

    case RequestStatus.Error:
      return <ErrorTryAgain onTryAgain={() => update()} />;

    default:
      return null;
  }
};
